import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { inputCls, labelCls, btn } from "@/components/admin/ui";

const Box = ({ title, children }) => (
  <div className="bg-surface border border-hairline rounded-xl p-5 mb-4">
    <h4 className="font-display text-base mb-2">{title}</h4>
    {children}
  </div>
);

const TextRow = ({ label, value, onChange, area }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {area ? (
      <textarea className={`${inputCls} min-h-[80px]`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const HeroBox = ({ s, upd }) => (
  <Box title="Hero">
    <TextRow label="Prompt bar" value={s.hero.prompt} onChange={(v) => upd(["hero", "prompt"], v)} />
    <div className="grid grid-cols-2 gap-3">
      <TextRow label="Name line 1" value={s.hero.name_line1} onChange={(v) => upd(["hero", "name_line1"], v)} />
      <TextRow label="Name line 2" value={s.hero.name_line2} onChange={(v) => upd(["hero", "name_line2"], v)} />
    </div>
    <TextRow label="Sub (bold)" value={s.hero.sub_bold} onChange={(v) => upd(["hero", "sub_bold"], v)} />
    <TextRow label="Sub text" value={s.hero.sub_text} onChange={(v) => upd(["hero", "sub_text"], v)} area />
    <TextRow label="Scroll hint" value={s.hero.scroll_hint} onChange={(v) => upd(["hero", "scroll_hint"], v)} />
  </Box>
);

const WorkBox = ({ s, upd }) => (
  <>
    <Box title="Marquee (one item per line)">
      <textarea
        className={`${inputCls} min-h-[110px]`}
        value={(s.marquee || []).join("\n")}
        onChange={(e) => upd(["marquee"], e.target.value.split("\n").filter(Boolean))}
      />
    </Box>
    <Box title="Work section">
      <TextRow label="Eyebrow" value={s.work.eyebrow} onChange={(v) => upd(["work", "eyebrow"], v)} />
      <TextRow label="Heading" value={s.work.heading} onChange={(v) => upd(["work", "heading"], v)} />
      <TextRow label="Footer line" value={s.work.footer_line} onChange={(v) => upd(["work", "footer_line"], v)} />
    </Box>
  </>
);

const ProcessBox = ({ s, upd }) => (
  <Box title="Process">
    <TextRow label="Eyebrow" value={s.process.eyebrow} onChange={(v) => upd(["process", "eyebrow"], v)} />
    <TextRow label="Heading" value={s.process.heading} onChange={(v) => upd(["process", "heading"], v)} />
    {(s.process.steps || []).map((st, i) => (
      <div key={st.number || `step-${i}`} className="border border-hairline rounded-lg p-3 mt-3">
        <div className="grid grid-cols-2 gap-3">
          <TextRow label="Number" value={st.number} onChange={(v) => upd(["process", "steps", i, "number"], v)} />
          <TextRow label="Label" value={st.label} onChange={(v) => upd(["process", "steps", i, "label"], v)} />
        </div>
        <TextRow label="Title" value={st.title} onChange={(v) => upd(["process", "steps", i, "title"], v)} />
        <TextRow label="Body" value={st.body} onChange={(v) => upd(["process", "steps", i, "body"], v)} area />
      </div>
    ))}
  </Box>
);

const AboutBox = ({ s, upd }) => (
  <Box title="About">
    <TextRow label="Eyebrow" value={s.about.eyebrow} onChange={(v) => upd(["about", "eyebrow"], v)} />
    <TextRow label="Lede (wrap accent words in *asterisks*)" value={s.about.lede} onChange={(v) => upd(["about", "lede"], v)} area />
    <TextRow label="Body" value={s.about.body} onChange={(v) => upd(["about", "body"], v)} area />
    <TextRow label="Brands label" value={s.about.brands_label} onChange={(v) => upd(["about", "brands_label"], v)} />
    <label className={labelCls}>Brands (one per line)</label>
    <textarea className={`${inputCls} min-h-[110px]`} value={(s.about.brands || []).join("\n")} onChange={(e) => upd(["about", "brands"], e.target.value.split("\n").filter(Boolean))} />
    <label className={labelCls}>Capabilities (name | tools per line)</label>
    <textarea
      className={`${inputCls} min-h-[140px]`}
      value={(s.about.capabilities || []).map((c) => `${c.name} | ${c.tools}`).join("\n")}
      onChange={(e) => upd(["about", "capabilities"], e.target.value.split("\n").filter(Boolean).map((l) => {
        const [name, tools] = l.split("|"); return { name: (name || "").trim(), tools: (tools || "").trim() };
      }))}
    />
  </Box>
);

const ContactBox = ({ s, upd }) => (
  <Box title="Contact">
    <TextRow label="Eyebrow" value={s.contact.eyebrow} onChange={(v) => upd(["contact", "eyebrow"], v)} />
    <TextRow label="Heading" value={s.contact.heading} onChange={(v) => upd(["contact", "heading"], v)} />
    <TextRow label="Email" value={s.contact.email} onChange={(v) => upd(["contact", "email"], v)} />
    <div className="grid grid-cols-3 gap-3">
      <TextRow label="Instagram" value={s.contact.instagram_handle} onChange={(v) => upd(["contact", "instagram_handle"], v)} />
      <TextRow label="Location" value={s.contact.location} onChange={(v) => upd(["contact", "location"], v)} />
      <TextRow label="Open to" value={s.contact.open_to} onChange={(v) => upd(["contact", "open_to"], v)} />
    </div>
  </Box>
);

const FooterNavBox = ({ s, upd }) => (
  <Box title="Footer & nav">
    <div className="grid grid-cols-2 gap-3">
      <TextRow label="Footer left" value={s.footer.left} onChange={(v) => upd(["footer", "left"], v)} />
      <TextRow label="Footer right" value={s.footer.right} onChange={(v) => upd(["footer", "right"], v)} />
    </div>
    <TextRow label="Logo text" value={s.nav.logo} onChange={(v) => upd(["nav", "logo"], v)} />
    <TextRow label="Instagram URL" value={s.nav.instagram_url} onChange={(v) => upd(["nav", "instagram_url"], v)} />
  </Box>
);

export default function CopyTab({ site, reload }) {
  const [s, setS] = useState(JSON.parse(JSON.stringify(site)));
  const [saving, setSaving] = useState(false);
  useEffect(() => setS(JSON.parse(JSON.stringify(site))), [site]);

  const upd = (path, value) => {
    setS((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/site", s); toast.success("Page copy saved"); reload(); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="pb-24">
      <HeroBox s={s} upd={upd} />
      <WorkBox s={s} upd={upd} />
      <ProcessBox s={s} upd={upd} />
      <AboutBox s={s} upd={upd} />
      <ContactBox s={s} upd={upd} />
      <FooterNavBox s={s} upd={upd} />
      <div className="fixed bottom-6 right-6 z-30">
        <button onClick={save} disabled={saving} className={`${btn} bg-lav text-ink shadow-lg`} data-testid="save-copy">
          {saving ? "saving…" : "Save page copy"}
        </button>
      </div>
    </div>
  );
}
